import { z } from 'zod';

/** Where a completion CTA points — same-app path vs. somewhere else entirely. */
export const COMPLETION_CTA_DESTINATIONS = ['internal', 'external'] as const;

export type CompletionCtaDestination =
  (typeof COMPLETION_CTA_DESTINATIONS)[number];

/**
 * What finished the run. `final_section` = the API completed it the moment the
 * last content section was submitted; `completion_screen` = the guest reached
 * the Completion screen and the client's own save finished it (the fallback
 * for classes with no content after sign-in, and for a Branch that jumps
 * straight to the Completion section).
 */
export const RUN_COMPLETION_SOURCES = [
  'final_section',
  'completion_screen',
] as const;

export type RunCompletionSource = (typeof RUN_COMPLETION_SOURCES)[number];

/** Which step of the server-side completion threw. */
export const RUN_COMPLETION_FAILURE_STAGES = [
  'sections',
  'completion_submission',
  'stamp',
] as const;

export type RunCompletionFailureStage =
  (typeof RUN_COMPLETION_FAILURE_STAGES)[number];

/**
 * Why a guest check-in did not mint a token. `church_not_live` (the church's
 * plan ended — `assertGuestChurchLive`) and `class_not_published` (a non-admin
 * on a class with no open published snapshot — `assertCoursePublished`) are the
 * two deliberate refusals; every other value names the awaited step that
 * THREW, in resolver order.
 */
export const CHECKIN_FAILURE_REASONS = [
  'church_not_live',
  'church_check_failed',
  'guest_lookup_failed',
  'guest_create_failed',
  'admin_lookup_failed',
  'class_not_published',
  'publish_check_failed',
  'checkin_create_failed',
  'attribution_lookup_failed',
  'token_failed',
] as const;

export type CheckinFailureReason = (typeof CHECKIN_FAILURE_REASONS)[number];

/**
 * How the guest arrived at check-in. Mirrors CHECK_IN_VIA in
 * @onark/shared/checkInArrival (this package cannot depend on shared); the
 * api's check-in test drives both values through the catalog, so a drift
 * between the two lists fails there, not in prod.
 */
export const CHECKIN_VIA = ['class_link', 'form'] as const;

/**
 * Why a publish was refused or failed. The first three are the resolver's own
 * refusals, in the order it checks them; the last two are the awaited steps
 * that can throw (Stripe, then the `publish_course` DB function).
 */
export const COURSE_PUBLISH_FAILURE_REASONS = [
  'course_not_found',
  'no_payment_method',
  'no_active_subscription',
  'subscription_lookup_failed',
  'publish_failed',
] as const;

export type CoursePublishFailureReason =
  (typeof COURSE_PUBLISH_FAILURE_REASONS)[number];

/**
 * Why the admin UI walled a publish before it ever reached the mutation.
 * Deliberately a SUBSET of COURSE_PUBLISH_FAILURE_REASONS, spelled the
 * server's way rather than the button's (`needPlan` / `suspended`), so one
 * query over `classes.course_publish_blocked` + `classes.course_publish_failed`
 * counts every church that hit the billing wall regardless of which layer
 * stopped it. `catalog.test.ts` pins the subset relationship.
 */
export const COURSE_PUBLISH_BLOCKED_REASONS = [
  'no_payment_method',
  'no_active_subscription',
] as const;

export type CoursePublishBlockedReason =
  (typeof COURSE_PUBLISH_BLOCKED_REASONS)[number];

/** The Stripe statuses that let a church publish. */
export const PUBLISHABLE_SUBSCRIPTION_STATUSES = [
  'active',
  'trialing',
  'past_due',
] as const;

export const classesSchemas = {
  /**
   * The class-structure reader was asked for courses the church does not own
   * (a guest-detail or report request carrying another church's course ids).
   * The reader drops them and the caller refuses or omits; this is the only
   * record that a cross-church read was attempted. Ids only, no PII.
   */
  'classes.structure_ownership_miss': z.object({
    churchId: z.string(),
    courseIds: z.array(z.string()),
  }),

  /**
   * Submissions answered sections that resolve neither from the live
   * `Section` table nor from `_history.Section` within the church's courses,
   * so their answers have no structure to render against. Expected only for
   * data that predates history capture; a spike means a history gap.
   */
  'classes.structure_unresolvable': z.object({
    churchId: z.string(),
    sectionIds: z.array(z.string()),
  }),

  /**
   * A guest's check-in minted a NEW CheckIn row: a fresh run of a class (or an
   * admin previewing one — `adminCheckIn`, which always creates rather than
   * resumes).
   *
   * WHY THIS EXISTS. Check-in is the guest path's front door and, until this,
   * the server recorded nothing about it: the only signal was the client's
   * uncatalogued `guestLoggedIn` capture (Layout.tsx), which fires after the
   * mutation succeeds and so cannot see a refusal, a throw, or the difference
   * between a new run and a resumed one. Dashboards still read
   * `guestLoggedIn`; this is the server-side truth beside it.
   *
   * `newGuest` = the Guest row was created by this check-in (first-ever visit
   * to this church); `via` = the arrival the row froze (null when the client
   * predates the argument). No email: guest PII stays out of the catalog.
   */
  'classes.checkin_created': z.object({
    adminCheckIn: z.boolean(),
    checkInId: z.string(),
    churchId: z.string(),
    courseId: z.string(),
    newGuest: z.boolean(),
    sectionId: z.string(),
    via: z.enum(CHECKIN_VIA).nullable(),
  }),

  /**
   * A guest checked in to a class they already started within the last 30
   * days, and got THAT run's token back instead of a new row. Resuming is by
   * design (a returning guest keeps their answers); this event is what makes
   * the resume share visible, and what a "my guest lost their progress"
   * ticket can be checked against.
   */
  'classes.checkin_resumed': z.object({
    adminCheckIn: z.boolean(),
    checkInId: z.string(),
    churchId: z.string(),
    courseId: z.string(),
  }),

  /**
   * The check-in mutation did NOT return a token. `church_not_live` is the
   * walled-church refusal (how many guests hit a church whose plan ended);
   * every other reason is an unexpected throw, named by the step. The error
   * itself reaches Rollbar through the Apollo plugin; this is the counter.
   * ADR 0002 failure-branch instrumentation on a guest path.
   */
  'classes.checkin_failed': z.object({
    churchId: z.string(),
    courseId: z.string(),
    reason: z.enum(CHECKIN_FAILURE_REASONS),
    sectionId: z.string(),
  }),

  /**
   * A church published a class: the `publish_course` DB function ran, so a
   * live (Historical*) version now exists for guests to check in to.
   *
   * WHY THIS EXISTS. Publish is the activation moment of the whole product
   * (a church that never publishes never gets a guest), and the only prior
   * record of it was a Customer.io attribute that has already missed real
   * publishes once. `subscriptionStatus` says which billing state the church
   * published under, so trial publishes and past-due publishes are separable.
   */
  'classes.course_published': z.object({
    churchId: z.string(),
    courseId: z.string(),
    subscriptionStatus: z.enum(PUBLISHABLE_SUBSCRIPTION_STATUSES),
  }),

  /**
   * The publish mutation threw. The three refusals are the billing wall
   * doing its job; the two `*_failed` reasons are outages. `churchId` is
   * absent only for `course_not_found`, where there is no church to name.
   * ADR 0002 failure-branch instrumentation.
   *
   * ⚠️ This event does NOT count churches that tried to publish without a
   * plan — the admin button walls them first and never calls the mutation, so
   * `no_payment_method` and `no_active_subscription` only land here when
   * something bypasses the UI (a direct API call, or a stale page whose
   * subscription lapsed after render). Measured over the three days after
   * this event shipped: 27 publishes, 0 failures, and 1 wall that only the
   * client saw. The wall itself is `classes.course_publish_blocked`; count
   * both to get every church that hit it.
   */
  'classes.course_publish_failed': z.object({
    churchId: z.string().optional(),
    courseId: z.string(),
    reason: z.enum(COURSE_PUBLISH_FAILURE_REASONS),
  }),

  /**
   * A church clicked Publish and the admin UI sent them to billing instead:
   * no payment method on file, or no qualifying subscription. The mutation is
   * never called, so this is the ONLY record of the attempt.
   *
   * WHY THIS EXISTS. `classes.course_publish_failed` was built to count the
   * billing wall and cannot see it — `PublishBtn` evaluates the same two
   * conditions client-side and redirects. This is the moment a church wants
   * to go live and is told to pay first, which is exactly the moment a nudge
   * would land, and it was reaching PostHog only as an uncatalogued
   * `publishBlocked` capture with no schema and no test behind it.
   */
  'classes.course_publish_blocked': z.object({
    churchId: z.string(),
    courseId: z.string(),
    reason: z.enum(COURSE_PUBLISH_BLOCKED_REASONS),
  }),
  /**
   * A guest clicked the call to action on a class's completion screen.
   *
   * WHY THIS EXISTS. `classCompleted` already says someone reached the last
   * screen. Without this event, "finished and did nothing" and "finished and
   * went on to sign up" are the same shape in the data — the CTA is the only
   * exit from the end of a class, and an unmeasured exit is exactly the blind
   * spot ADR 0002 exists to close.
   *
   * The URL itself is deliberately absent: it is church-authored free text, and
   * `sectionId` already identifies which CTA fired (a completion section holds
   * at most one).
   */
  'classes.completion_cta_clicked': z.object({
    courseId: z.string(),
    destination: z.enum(COMPLETION_CTA_DESTINATIONS),
    sectionId: z.string(),
  }),

  /**
   * A guest's run of a class was recorded complete: CheckIn.completedAt is
   * stamped and the Completion section's submission is finished (which is
   * what fans out that section's automations).
   *
   * WHY THIS EXISTS. Completion used to be stamped only by the Completion
   * screen's mount (client-side). The class-completion study found 2,311
   * real guest runs (~4% of runs, 13.6% of abandonments) whose guests
   * submitted every content section and never loaded that screen — recorded
   * incomplete, automations never fired, invisible. The API now completes
   * the run when the last content section is submitted; `source` says which
   * path did it, so the share the server rescues is measurable and the
   * client fallback can be retired when it reads zero. Fires once per run:
   * it rides the Completion submission's FINISHED transition, which only
   * happens once.
   */
  'classes.run_completed': z.object({
    adminCheckIn: z.boolean(),
    checkInId: z.string(),
    churchId: z.string(),
    courseId: z.string(),
    /** The Completion section whose submission was finished. */
    sectionId: z.string(),
    source: z.enum(RUN_COMPLETION_SOURCES),
  }),

  /**
   * The server-side completion above THREW after the guest's own section
   * submission was already saved. The save still succeeds (the guest must
   * never lose their answers to a completion bookkeeping failure), so
   * without this the run silently falls back to the pre-fix behaviour —
   * exactly the gap this event's sibling was added to close. ADR 0002
   * failure-branch instrumentation; the error itself goes to Rollbar.
   */
  'classes.run_completion_failed': z.object({
    checkInId: z.string(),
    churchId: z.string(),
    courseId: z.string(),
    stage: z.enum(RUN_COMPLETION_FAILURE_STAGES),
  }),

  /**
   * A section finish that would have re-run its automations was suppressed.
   * `resend` = the save repeated one that had already finished this
   * submission (a retake's `FINISHED: 0` arriving twice, e.g. a lost response
   * the client re-sent); `lost_race` = an overlapping request stamped
   * FINISHED first, so this one did not queue a second CheckForAutomations.
   *
   * WHY THIS EXISTS. Finishing a section queues its automations (emails, PCO
   * writes), so a second finish is a duplicate email to a guest. Both guards
   * are silent no-ops by design (the guest's save still succeeds), so without
   * this the fix is invisible: no way to tell it is firing, or to notice a
   * client that starts re-sending far more than expected. ADR 0002
   * failure-branch instrumentation on a guest path.
   */
  'classes.section_finish_skipped': z.object({
    churchId: z.string(),
    reason: z.enum(['resend', 'lost_race']),
    submissionId: z.string(),
  }),

  /**
   * The server-side section save THREW (anything but a deliberate GraphQLError
   * refusal) and the guest's answers were not stored. The guest sees the same
   * failure as before; this is the counter and the Rollbar grouping key.
   *
   * WHY THIS EXISTS. On 2026-10-01 21:00-21:10 UTC a database read-only
   * window (Postgres 25006) failed guest writes across at least 3 churches,
   * and the only trace was the generic GraphQLError plugin, which filed it
   * under an unrelated old Rollbar item. `errorCode` is the Postgres SQLSTATE
   * (or Prisma code) so an outage reads differently from a code bug. No
   * message and no answers: guest data stays out of the catalog. ADR 0002
   * failure-branch instrumentation on a guest path.
   */
  'classes.section_save_failed': z.object({
    checkInId: z.string(),
    churchId: z.string(),
    /** SQLSTATE (e.g. `25006`) or Prisma code (e.g. `P2010`) when the error
     *  carried one. */
    errorCode: z.string().optional(),
    sectionId: z.string(),
  }),

  /**
   * A section save was rejected because it repeated a ministry pick this run
   * already made in another Ministries (TeamSuggestion) question of the
   * class — see @onark/shared/teamSelection.
   *
   * WHY THIS EXISTS. The guest form hides teams already chosen elsewhere in
   * the run, so this rule should almost never fire client-side; a rejection
   * here means that guard was missed — most likely a guest holding a stale
   * page across a picker update, or a hand-built request. It is the only
   * signal that the server-side enforcement is actually being hit, which
   * matters both to confirm the fix is working and to catch a client
   * regression that stops hiding chosen teams. ADR 0002 failure-branch
   * instrumentation on a guest path.
   */
  'classes.team_selection_rejected': z.object({
    checkInId: z.string(),
    churchId: z.string(),
    courseId: z.string(),
    sectionId: z.string(),
    /** The team(s) the guest tried to pick more than once. */
    teams: z.array(z.string()),
  }),

  /**
   * The duplicate-ministry check above could not run (its check-in or input
   * lookups threw) and the save went through unchecked.
   *
   * WHY THIS EXISTS. The check fails open on purpose: blocking a guest's save
   * over a ministry question is worse than one duplicate. But a validation
   * failure is not bookkeeping — nothing can be backfilled once the guest has
   * gone home with two identical picks — so a silent fail-open would read as
   * "the fix did not ship" the next time a church complains. This is the
   * only signal that the enforcement is being bypassed, per ADR 0002's rule
   * that failure branches on guest paths emit.
   */
  'classes.team_selection_check_failed': z.object({
    checkInId: z.string(),
    churchId: z.string(),
    sectionId: z.string(),
  }),

  /**
   * A guest flipped the CC button on the branded video player.
   *
   * WHY THIS EXISTS. Captions shipped broken once already — the engine-level
   * toggle read "on" while YouTube painted the text inside the player's
   * chrome-hiding crop, and nothing measured whether captions were ever
   * SEEN. Now the stage renders captions itself; this event is the usage
   * signal that tells us whether the accessibility surface is real (and
   * whether Vimeo/file captions are worth building next).
   */
  'classes.video_captions_toggled': z.object({
    courseId: z.string(),
    /** 'cues' = our branded caption line; 'engine' = YouTube's renderer with
     *  the crop released (the pot-gate fallback — watermark visible). Tells
     *  us how often guests see the degraded mode. */
    mode: z.enum(['cues', 'engine']),
    on: z.boolean(),
    sectionId: z.string(),
  }),

  /**
   * The captions proxy (app/api/captions) FAILED for a guest — network, or
   * YouTube refusing our server (datacenter-IP bot checks: CONFIRMED for
   * Railway 2026-08-25, so on prod this fires routinely). Distinct from
   * "video has no captions", which is normal and deliberately unmeasured.
   * The stage usually RECOVERS the CC button client-side via the engine
   * probe afterwards — so this measures proxy health, not guest-facing
   * caption loss; pair with video_captions_toggled mode='engine' for the
   * recovery rate. ADR 0002 failure-branch instrumentation.
   */
  'classes.video_captions_unavailable': z.object({
    courseId: z.string(),
    sectionId: z.string(),
  }),

  /**
   * The "email me my results" send FAILED after a guest finished a Test
   * section. The send is fire-and-forget on purpose: it used to sit between
   * the COMPLETE save and the navigation to the next section, so a slow or
   * failing send left the guest parked on the results screen with the run
   * already marked complete (579 stranded runs in prod, 2026-09-03). Now the
   * guest moves on regardless and this event is the only trace that the
   * email never went out. ADR 0002 failure-branch instrumentation.
   */
  'classes.test_results_email_failed': z.object({
    courseId: z.string(),
    /** `error.message` when the failure carried one; kept short and free
     *  of guest data. */
    reason: z.string(),
    sectionId: z.string(),
  }),

  /**
   * The save that marks a guest section COMPLETE (Next on a Test results
   * screen or a Video) FAILED. That save goes straight to the API rather than
   * through rc-form's submit handler, so nothing reported it: the rejection
   * was unhandled, the Next button showed success, and on a Test the guest
   * stayed on the results screen with no message (Rollbar onark-frontend
   * #576, a phone with no signal). The guest now sees the error and can press
   * Next again; this event is the count of how often that happens. ADR 0002
   * failure-branch instrumentation.
   */
  'classes.section_complete_save_failed': z.object({
    courseId: z.string(),
    /** The server's first error message, else `error.message` ("Load failed"
     *  when the request never left the phone), without the request body
     *  graphql-request appends to it. */
    reason: z.string(),
    sectionId: z.string(),
  }),
};
